import { IonPage, IonContent } from '@ionic/react';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { YummyText } from '../../../components/YummyText';
import { resetPassword } from '../../../utils/authApi';
import { getCookie } from '../../../utils/cookies';

const ResetPassword = () => {
    const history = useHistory();
    const location = useLocation();
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        // Extract token from URL query params
        const params = new URLSearchParams(location.search);
        const resetToken = params.get('token');

        if (!resetToken) {
            setError('Invalid or missing reset token. Please request a new password reset link.');
        } else {
            setToken(resetToken);
        }
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!token) {
            setError('Invalid reset token');
            return;
        }

        setLoading(true);

        try {
            await resetPassword({ token, password, confirmPassword });
            setSuccess(true);
            // Redirect to role-specific login after 3 seconds (respect ?role=customer|rider)
            setTimeout(() => {
                const params = new URLSearchParams(location.search);
                let role = params.get('role');
                // Fallback: try cookies or localStorage
                if (!role) {
                    role = getCookie('userRole') || getCookie('user_type');
                    if (!role) {
                        try {
                            const stored = JSON.parse(localStorage.getItem('user_data') || '{}');
                            role = stored?.role || role;
                        } catch (e) {
                            // ignore
                        }
                    }
                }

                const message = encodeURIComponent('Password reset successful. Please log in with your new password.');
                const dest = (role === 'customer' || role === 'rider')
                    ? `/auth/${role}/login?message=${message}`
                    : `/auth/role-select?message=${message}`;
                history.push(dest);
            }, 3000);
        } catch (err) {
            console.error('Reset password error:', err);
            // Prefer server-provided validation messages when available
            const serverMsg = err?.data?.message || err?.message || (err?.data && JSON.stringify(err.data)) || 'Failed to reset password. The link may have expired.';
            setError(serverMsg);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <IonPage>
                <IonContent className="ion-padding">
                    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <YummyText>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Password Reset Successful!</h2>
                                <p className="text-gray-600 mb-6">
                                    Your password has been successfully reset. You can now log in with your new password.
                                </p>
                                <p className="text-sm text-gray-500">
                                    Redirecting to login page...
                                </p>
                            </YummyText>
                        </div>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonContent className="ion-padding">
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                    <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
                        <YummyText>
                            <div className="text-center mb-8">
                                <h1 className="text-3xl font-semibold text-gray-900 mb-2">Reset Password</h1>
                                <p className="text-gray-600">
                                    Enter your new password below.
                                </p>
                            </div>
                        </YummyText>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <YummyText>
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            required
                                            minLength={8}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00D68F] focus:border-transparent pr-12"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-4">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            required
                                            minLength={8}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00D68F] focus:border-transparent pr-12"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showConfirmPassword ? (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading || !token || !password || !confirmPassword}
                                    className="w-full bg-[#00D68F] text-white py-3 mt-3 rounded-xl font-medium hover:bg-[#00B876] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Resetting Password...' : 'Reset Password'}
                                </button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const params = new URLSearchParams(location.search);
                                            let role = params.get('role');
                                            if (!role) role = getCookie('userRole') || getCookie('user_type');
                                            if (!role) {
                                                try {
                                                    const stored = JSON.parse(localStorage.getItem('user_data') || '{}');
                                                    role = stored?.role || role;
                                                } catch (e) { }
                                            }
                                            if (role === 'customer' || role === 'rider') history.push(`/auth/${role}/login`);
                                            else history.push('/auth/role-select');
                                        }}
                                        className="text-[#00D68F] hover:text-[#00B876] font-medium text-sm"
                                    >
                                        ← Back to Login
                                    </button>
                                </div>
                            </YummyText>
                        </form>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default ResetPassword;
